// Local queue (ERP rule 5 / BRIEF rule 12): while offline the cashier can still
// draft INVOICES and RECEIPT VOUCHERS only. Each draft is stored in IndexedDB
// with a temporary number (shown with the «محلي · رقم مؤقت» pill) and replayed
// in creation order when the connection returns:
//
//   POST /api/erp/sync/documents
//   { documents: [{ client_id, temp_number, type, payload, base_sync_version }] }
//   → 200 { results: { <client_id>: { number_text: "#ق-0152" } | { conflict: { server_version, client_version, detail? } } } }
//
// 409 is per item, never for the batch (erp-core internal/localqueue). A
// conflict is a PRIMARY state (accounts/sync--conflict) the person resolves —
// never silently retried. Network failure leaves items pending.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { useEffect, useState } from 'react';
import { ApiError, erpApi } from './api';
import { onlineStore } from './online';
import { readStore, writeStore } from './storage';

export type QueueDocType = 'invoice' | 'receipt_voucher';
export type QueueState = 'pending' | 'syncing' | 'conflict' | 'synced';

export interface QueueConflict {
  code?: string;
  detail?: string;
  server_version?: number;
  client_version?: number;
  /** erp-core sync conflict: invoice_changed | invoice_void | balance_changed. */
  reason?: string;
  server_amount_minor?: number;
  client_amount_minor?: number;
  document_id?: string;
  /** HTTP status of a non-retryable rejection. */
  status?: number;
}

export interface QueueItem<P = unknown> {
  client_id: string;
  business_id: string;
  type: QueueDocType;
  temp_number: string;
  payload: P;
  base_sync_version: number;
  state: QueueState;
  created_at: string;
  updated_at: string;
  attempts: number;
  /** Device-local creation order (replay order). */
  seq: number;
  /** Final server number once synced (e.g. '#ق-0152'). */
  number_text?: string;
  conflict?: QueueConflict;
}

interface QueueDB extends DBSchema {
  items: { key: string; value: QueueItem; indexes: { by_seq: number; by_business: string } };
  meta: { key: string; value: { key: string; value: string | number } };
}

const DB_NAME = 'ehjizli-erp';
const DB_VERSION = 1;
const LAST_SYNC_KEY = 'erp.queue.last_sync_at';

let dbPromise: Promise<IDBPDatabase<QueueDB>> | null = null;

function db(): Promise<IDBPDatabase<QueueDB>> {
  if (!dbPromise) {
    dbPromise = openDB<QueueDB>(DB_NAME, DB_VERSION, {
      upgrade(d) {
        const items = d.createObjectStore('items', { keyPath: 'client_id' });
        items.createIndex('by_seq', 'seq');
        items.createIndex('by_business', 'business_id');
        d.createObjectStore('meta', { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Stable short id for this browser/device (part of every temp number). */
async function deviceTag(d: IDBPDatabase<QueueDB>): Promise<string> {
  const row = await d.get('meta', 'device');
  if (row && typeof row.value === 'string') return row.value;
  const tag = uuid().replace(/-/g, '').slice(0, 4).toUpperCase();
  await d.put('meta', { key: 'device', value: tag });
  return tag;
}

async function nextSeq(d: IDBPDatabase<QueueDB>): Promise<number> {
  const tx = d.transaction('meta', 'readwrite');
  const row = await tx.store.get('seq');
  const n = (typeof row?.value === 'number' ? row.value : 0) + 1;
  await tx.store.put({ key: 'seq', value: n });
  await tx.done;
  return n;
}

// ---------------------------------------------------------------- status --

export interface SyncStatus {
  offline: boolean;
  /** Drafts waiting to sync (pending + syncing). */
  pending: number;
  /** Drafts the server refused (409 per item) — the primary conflict state. */
  conflicts: number;
  /** Epoch ms of the last successful replay, or null. */
  lastSyncAt: number | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let counts = { pending: 0, conflicts: 0 };
let lastSyncAt: number | null = Number(readStore(LAST_SYNC_KEY) ?? 0) || null;

function emit() {
  listeners.forEach((l) => l());
}

async function recount(): Promise<void> {
  const all = await (await db()).getAll('items');
  counts = {
    pending: all.filter((i) => i.state === 'pending' || i.state === 'syncing').length,
    conflicts: all.filter((i) => i.state === 'conflict').length,
  };
  emit();
}

export function getSyncStatus(): SyncStatus {
  return { offline: !onlineStore.isOnline(), pending: counts.pending, conflicts: counts.conflicts, lastSyncAt };
}

export function subscribeSyncStatus(l: Listener): () => void {
  listeners.add(l);
  const off = onlineStore.subscribe(l);
  return () => {
    listeners.delete(l);
    off();
  };
}

/** { offline, pending, conflicts, lastSyncAt } — drives the top-bar sync pill. */
export function useSyncStatus(): SyncStatus {
  const [s, setS] = useState<SyncStatus>(getSyncStatus);
  useEffect(() => {
    const update = () => setS(getSyncStatus());
    const off = subscribeSyncStatus(update);
    recount().catch(() => {});
    update();
    return off;
  }, []);
  return s;
}

// ----------------------------------------------------------------- queue --

/** Draft a document locally. Returns the stored item with its temporary number. */
export async function enqueue<P>(
  type: QueueDocType,
  payload: P,
  opts: { businessId: string; baseSyncVersion?: number },
): Promise<QueueItem<P>> {
  const d = await db();
  const [tag, seq] = [await deviceTag(d), await nextSeq(d)];
  const now = new Date().toISOString();
  const item: QueueItem<P> = {
    client_id: uuid(),
    business_id: opts.businessId,
    type,
    temp_number: `L-${tag}-${String(seq).padStart(4, '0')}`,
    payload,
    base_sync_version: opts.baseSyncVersion ?? 0,
    state: 'pending',
    created_at: now,
    updated_at: now,
    attempts: 0,
    seq,
  };
  await d.put('items', item as QueueItem);
  await recount();
  return item;
}

/** All queued items (optionally one business), oldest first. */
export async function listQueue(businessId?: string): Promise<QueueItem[]> {
  const all = await (await db()).getAllFromIndex('items', 'by_seq');
  return businessId ? all.filter((i) => i.business_id === businessId) : all;
}

export async function getQueueItem(clientId: string): Promise<QueueItem | undefined> {
  return (await db()).get('items', clientId);
}

/** Drop a draft (the person chose to discard a conflict). */
export async function discardQueueItem(clientId: string): Promise<void> {
  await (await db()).delete('items', clientId);
  await recount();
}

/** Put a conflicted draft back in line (after the person edited it). */
export async function requeueItem<P>(clientId: string, payload?: P, baseSyncVersion?: number): Promise<void> {
  const d = await db();
  const item = await d.get('items', clientId);
  if (!item) return;
  item.state = 'pending';
  item.conflict = undefined;
  if (payload !== undefined) item.payload = payload;
  if (baseSyncVersion !== undefined) item.base_sync_version = baseSyncVersion;
  item.updated_at = new Date().toISOString();
  await d.put('items', item);
  await recount();
}

/** Remove synced items (they live on the server now). */
export async function purgeSynced(): Promise<number> {
  const d = await db();
  const synced = (await d.getAll('items')).filter((i) => i.state === 'synced');
  for (const i of synced) await d.delete('items', i.client_id);
  await recount();
  return synced.length;
}

export interface SyncDocument {
  client_id: string;
  temp_number: string;
  type: QueueDocType;
  payload: unknown;
  base_sync_version: number;
}
export type SyncItemResult = { number_text: string } | { conflict: QueueConflict };
// businessId is the ONE value that lands in X-Business-Id for this call
// (api.ts headersFor): every doc in `docs` must belong to it. replayQueue
// enforces that by grouping before it ever calls send (2026-10-26 security
// pass, finding M3 — a mixed batch silently re-homed one business's drafts
// into another's books).
export type SyncSender = (docs: SyncDocument[], businessId: string) => Promise<Record<string, SyncItemResult>>;

const DEVICE_KEY = 'erp.docs.device_id';

/** Stable id of this browser; erp-core keeps it on every queue row (`device_id`). */
export function syncDeviceId(): string {
  const have = readStore(DEVICE_KEY);
  if (have) return have;
  const rnd =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  const id = `web-${rnd}`;
  writeStore(DEVICE_KEY, id);
  return id;
}

/** One element of erp-core's `POST /v1/erp/sync/documents` `results` (internal/localqueue). */
export interface SyncResultWire {
  client_id: string;
  status: 'synced' | 'conflict' | 'rejected';
  number_display?: string;
  conflict?: { reason?: string; server_version?: number; client_version?: number; server_amount_minor?: number; client_amount_minor?: number; document_id?: string };
  problem?: { status: number; code: string; detail: string };
  retryable?: boolean;
}

/**
 * erp-core's answer for one document → the queue's state:
 *   synced                   → { number_text: number_display }
 *   conflict                 → { conflict: {code:'conflict', reason, versions, amounts} } (a primary state)
 *   rejected, not retryable  → { conflict: {code, detail, status} } (the person sees it as a conflict)
 *   retryable / unknown      → null: left pending, tried again on the next replay
 */
export function mapSyncResult(r: SyncResultWire): SyncItemResult | null {
  if (r.status === 'synced' && r.number_display) return { number_text: r.number_display };
  if (r.status === 'conflict' && r.conflict) {
    const c = r.conflict;
    return {
      conflict: {
        code: 'conflict',
        reason: c.reason,
        server_version: c.server_version,
        client_version: c.client_version,
        server_amount_minor: c.server_amount_minor,
        client_amount_minor: c.client_amount_minor,
        document_id: c.document_id,
      },
    };
  }
  if (r.status === 'rejected' && !r.retryable && r.problem) return { conflict: { code: r.problem.code, detail: r.problem.detail, status: r.problem.status } };
  return null;
}

/**
 * Default sender — erp-core's contract (internal/localqueue):
 *   POST /api/erp/sync/documents {device_id, items:[{client_id, temp_number, type, payload, base_sync_version}]}
 *   → {results:[{client_id, status, number_display, conflict, problem, retryable}], by_client_id, summary}
 * 409 per item, never for the batch.
 */
export const defaultSender: SyncSender = async (docs, businessId) => {
  const res = await erpApi.post<unknown>(
    '/sync/documents',
    {
      device_id: syncDeviceId(),
      items: docs.map((d) => ({ client_id: d.client_id, temp_number: d.temp_number, type: d.type, payload: d.payload, base_sync_version: d.base_sync_version })),
    },
    // Explicit, not the ambient session default: replayQueue already
    // grouped `docs` by their OWN business_id, so this call must carry
    // exactly that one — never whichever business happens to be active in
    // the browser when the batch actually fires.
    { businessId },
  );
  return normalizeSyncResults(res);
};

/** erp-core's `results[]` (status per item); also tolerates the older map / number_text shapes. */
export function normalizeSyncResults(res: unknown): Record<string, SyncItemResult> {
  const out: Record<string, SyncItemResult> = {};
  const root = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
  const results = (root.results ?? root) as unknown;
  const put = (id: unknown, v: Record<string, unknown>) => {
    if (typeof id !== 'string') return;
    if (typeof v.status === 'string') {
      const m = mapSyncResult({ ...(v as unknown as SyncResultWire), client_id: id });
      if (m) out[id] = m;
    } else if (typeof v.number_text === 'string') out[id] = { number_text: v.number_text };
    else if (v.conflict && typeof v.conflict === 'object') out[id] = { conflict: v.conflict as QueueConflict };
  };
  if (Array.isArray(results)) {
    for (const r of results) if (r && typeof r === 'object') put((r as Record<string, unknown>).client_id, r as Record<string, unknown>);
  } else if (results && typeof results === 'object') {
    for (const [id, v] of Object.entries(results as Record<string, unknown>)) if (v && typeof v === 'object') put(id, v as Record<string, unknown>);
  }
  return out;
}

export interface ReplaySummary {
  synced: number;
  conflicts: number;
  remaining: number;
  /** temp_number → final number_text, for «تمت المزامنة» toasts. */
  renumbered: Record<string, string>;
}

let replaying: Promise<ReplaySummary> | null = null;

/** Send every pending draft (oldest first) in one batch. Coalesced; never throws. */
export function replayQueue(send: SyncSender = defaultSender): Promise<ReplaySummary> {
  if (replaying) return replaying;
  replaying = (async () => {
    const summary: ReplaySummary = { synced: 0, conflicts: 0, remaining: 0, renumbered: {} };
    const d = await db();
    const pending = (await d.getAllFromIndex('items', 'by_seq')).filter((i) => i.state === 'pending' || i.state === 'syncing');
    if (pending.length === 0) return summary;
    const now = () => new Date().toISOString();
    for (const i of pending) {
      i.state = 'syncing';
      i.attempts += 1;
      i.updated_at = now();
      await d.put('items', i);
    }
    await recount();

    // One X-Business-Id travels with the whole HTTP request (api.ts
    // headersFor), so a batch that mixed businesses would send every item
    // under whichever business happened to be active when the request
    // fired — misattributing another business's invoices and receipts into
    // it. Group by each item's OWN business_id (stamped at enqueue()) and
    // send one batch per business: a device or account pending on several
    // businesses at once still gets every draft synced, each under the
    // business it actually belongs to.
    const groups = new Map<string, typeof pending>();
    for (const i of pending) {
      const g = groups.get(i.business_id);
      if (g) g.push(i);
      else groups.set(i.business_id, [i]);
    }

    for (const [businessId, group] of groups) {
      let results: Record<string, SyncItemResult> = {};
      let batchError: ApiError | null = null;
      try {
        results = await send(
          group.map(({ client_id, temp_number, type, payload, base_sync_version }) => ({ client_id, temp_number, type, payload, base_sync_version })),
          businessId,
        );
      } catch (e) {
        batchError = e instanceof ApiError ? e : new ApiError({ status: 0, code: 'network' });
      }

      for (const i of group) {
        const r = results[i.client_id];
        if (r && 'number_text' in r) {
          i.state = 'synced';
          i.number_text = r.number_text;
          summary.synced += 1;
          summary.renumbered[i.temp_number] = r.number_text;
        } else if (r && 'conflict' in r) {
          i.state = 'conflict';
          i.conflict = r.conflict;
          summary.conflicts += 1;
        } else if (batchError && !batchError.isNetwork && batchError.status >= 400 && batchError.status < 500 && batchError.status !== 401) {
          // A refused batch is surfaced per document as a conflict (primary state).
          i.state = 'conflict';
          i.conflict = { code: batchError.code, detail: batchError.detail };
          summary.conflicts += 1;
        } else {
          i.state = 'pending'; // network / 5xx / 401 / missing result → try again later
          summary.remaining += 1;
        }
        i.updated_at = now();
        await d.put('items', i);
      }
    }
    if (summary.synced > 0) {
      lastSyncAt = Date.now();
      writeStore(LAST_SYNC_KEY, String(lastSyncAt));
    }
    await recount();
    return summary;
  })().finally(() => {
    replaying = null;
  });
  return replaying;
}

/** Replay now and whenever the connection comes back. Returns the stopper. */
export function startAutoReplay(send: SyncSender = defaultSender, onResult?: (s: ReplaySummary) => void): () => void {
  let wasOnline = onlineStore.isOnline();
  const run = () => {
    if (!onlineStore.isOnline()) return;
    void replayQueue(send).then((s) => onResult?.(s)).catch(() => {});
  };
  const off = onlineStore.subscribe(() => {
    const now = onlineStore.isOnline();
    if (now && !wasOnline) run();
    wasOnline = now;
  });
  recount().catch(() => {});
  run();
  return off;
}

/** Test hook: close + delete the database and reset counters. */
export async function __resetQueueForTests(): Promise<void> {
  if (dbPromise) {
    try {
      (await dbPromise).close();
    } catch {
      /* ignore */
    }
  }
  dbPromise = null;
  counts = { pending: 0, conflicts: 0 };
  lastSyncAt = null;
  writeStore(LAST_SYNC_KEY, null);
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}
